// API configuration and types
import { Expense, Income, CreditCard, PayoffPlan, Loan, ScheduleException } from '../types';

interface User {
  id: string;
  email: string;
  name?: string;
  balance: number;
  balanceAsOf?: string | null;
  lowBalanceThreshold?: number;
  createdAt: string;
  updatedAt: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface AuthResponse {
  token: string;
  user: User;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  name?: string;
}

// API Error class
export class ApiError extends Error {
  constructor(public message: string, public status?: number) {
    super(message);
    this.name = 'ApiError';
  }
}

// Generic API request function
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('cashintel-token');
  
  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        // If we can't parse the error, use the default message
      }
      throw new ApiError(errorMessage, response.status);
    }

    const data: ApiResponse<T> = await response.json();
    
    if (!data.success) {
      throw new ApiError(data.error || 'API request failed');
    }

    return data.data as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    
    // Network or other errors
    throw new ApiError(`Network error: ${(error as Error).message}`);
  }
}

// Authentication API
export const authApi = {
  async login(credentials: LoginData): Promise<AuthResponse> {
    const response = await apiRequest<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    
    // Store token in localStorage
    localStorage.setItem('cashintel-token', response.token);
    
    return response;
  },

  async register(userData: RegisterData): Promise<AuthResponse> {
    const response = await apiRequest<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    
    // Store token in localStorage
    localStorage.setItem('cashintel-token', response.token);
    
    return response;
  },

  logout() {
    localStorage.removeItem('cashintel-token');
  },

  getToken(): string | null {
    return localStorage.getItem('cashintel-token');
  },

  isAuthenticated(): boolean {
    return !!this.getToken();
  },

  async requestPasswordReset(email: string): Promise<{ resetToken?: string }> {
    return apiRequest('/auth/request-reset', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  async resetPassword(email: string, token: string, newPassword: string): Promise<void> {
    return apiRequest('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email, token, newPassword }),
    });
  },
};

// User API
export const userApi = {
  async getProfile(): Promise<User> {
    return apiRequest<User>('/user/profile');
  },

  async updateBalance(balance: number): Promise<User> {
    return apiRequest<User>('/user/balance', {
      method: 'PUT',
      body: JSON.stringify({ balance }),
    });
  },

  async updateThreshold(lowBalanceThreshold: number): Promise<User> {
    return apiRequest<User>('/user/profile', {
      method: 'PUT',
      body: JSON.stringify({ lowBalanceThreshold }),
    });
  },

  async updateProfile(name: string): Promise<User> {
    return apiRequest<User>('/user/profile', {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
  },

  async getDashboardData(): Promise<any> {
    return apiRequest<any>('/user/dashboard');
  },
};

// Expenses API
export const expensesApi = {
  async getAll(): Promise<Expense[]> {
    return apiRequest<Expense[]>('/expenses');
  },

  async getById(id: string): Promise<Expense> {
    return apiRequest<Expense>(`/expenses/${id}`);
  },

  async create(expenseData: any): Promise<Expense> {
    return apiRequest<Expense>('/expenses', {
      method: 'POST',
      body: JSON.stringify(expenseData),
    });
  },

  async update(id: string, expenseData: any): Promise<Expense> {
    return apiRequest<Expense>(`/expenses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(expenseData),
    });
  },

  async delete(id: string): Promise<void> {
    return apiRequest<void>(`/expenses/${id}`, {
      method: 'DELETE',
    });
  },
};

// Income API
export const incomeApi = {
  async getAll(): Promise<Income[]> {
    return apiRequest<Income[]>('/income');
  },

  async getById(id: string): Promise<Income> {
    return apiRequest<Income>(`/income/${id}`);
  },

  async create(incomeData: any): Promise<Income> {
    return apiRequest<Income>('/income', {
      method: 'POST',
      body: JSON.stringify(incomeData),
    });
  },

  async update(id: string, incomeData: any): Promise<Income> {
    return apiRequest<Income>(`/income/${id}`, {
      method: 'PUT',
      body: JSON.stringify(incomeData),
    });
  },

  async delete(id: string): Promise<void> {
    return apiRequest<void>(`/income/${id}`, {
      method: 'DELETE',
    });
  },
};

// Credit Cards API
export const creditCardsApi = {
  async getAll(): Promise<CreditCard[]> {
    return apiRequest<CreditCard[]>('/credit-cards');
  },

  async getById(id: string): Promise<CreditCard> {
    return apiRequest<CreditCard>(`/credit-cards/${id}`);
  },

  async create(cardData: any): Promise<CreditCard> {
    return apiRequest<CreditCard>('/credit-cards', {
      method: 'POST',
      body: JSON.stringify(cardData),
    });
  },

  async update(id: string, cardData: any): Promise<CreditCard> {
    return apiRequest<CreditCard>(`/credit-cards/${id}`, {
      method: 'PUT',
      body: JSON.stringify(cardData),
    });
  },

  async delete(id: string): Promise<void> {
    return apiRequest<void>(`/credit-cards/${id}`, {
      method: 'DELETE',
    });
  },
};

// --- Payoff Plans API ------------------------------------------------------

/** JSON carries dates as ISO strings; the PayoffPlan type declares Dates.
 *  Normalize at the boundary so no consumer has to remember to convert. */
function hydratePlan(raw: any): PayoffPlan {
  return {
    ...raw,
    startDate: new Date(raw.startDate),
    endDate: new Date(raw.endDate),
    originAsOf: new Date(raw.originAsOf),
    activatedAt: new Date(raw.activatedAt),
    targetDate: raw.targetDate ? new Date(raw.targetDate) : null,
    payments: (raw.payments ?? []).map((p: any) => ({
      ...p,
      dueDate: new Date(p.dueDate),
      confirmedAt: p.confirmedAt ? new Date(p.confirmedAt) : null,
    })),
  };
}

export interface PendingConfirmation {
  id: string;
  planId: string;
  dueDate: Date;
  amount: number;
  kind: string;
  plan: { id: string; creditCard: { id: string; name: string } };
}

export const payoffPlansApi = {
  async getAll(): Promise<PayoffPlan[]> {
    const rows = await apiRequest<any[]>('/payoff-plans');
    return rows.map(hydratePlan);
  },

  async create(planData: any): Promise<PayoffPlan> {
    return hydratePlan(
      await apiRequest<any>('/payoff-plans', {
        method: 'POST',
        body: JSON.stringify(planData),
      })
    );
  },

  /** Move one payment to another date. Everything else about it is unchanged. */
  async movePayment(paymentId: string, dueDate: Date): Promise<PayoffPlan> {
    return hydratePlan(
      await apiRequest<any>(`/payoff-plans/payments/${paymentId}/date`, {
        method: 'PATCH',
        body: JSON.stringify({ dueDate: dueDate.toISOString() }),
      })
    );
  },

  async update(id: string, planData: any): Promise<PayoffPlan> {
    return hydratePlan(
      await apiRequest<any>(`/payoff-plans/${id}`, {
        method: 'PUT',
        body: JSON.stringify(planData),
      })
    );
  },

  async setStatus(id: string, status: PayoffPlan['status']): Promise<PayoffPlan> {
    return hydratePlan(
      await apiRequest<any>(`/payoff-plans/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      })
    );
  },

  async delete(id: string): Promise<void> {
    return apiRequest<void>(`/payoff-plans/${id}`, { method: 'DELETE' });
  },

  /** Elapsed, unconfirmed payments — powers the "did you pay?" prompt. */
  async pendingConfirmations(): Promise<PendingConfirmation[]> {
    const rows = await apiRequest<any[]>('/payoff-plans/pending-confirmations');
    return rows.map(r => ({ ...r, dueDate: new Date(r.dueDate) }));
  },

  async confirmPayment(
    paymentId: string,
    status: 'CONFIRMED' | 'SKIPPED' | 'PLANNED',
    actualAmount?: number
  ): Promise<any> {
    return apiRequest<any>(`/payoff-plans/payments/${paymentId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status, actualAmount }),
    });
  },
};

/**
 * Occurrences moved off the day their rule generates.
 *
 * `move` is an upsert keyed on (kind, sourceId, originalDate): dragging the
 * same occurrence repeatedly replaces one record, and dropping it back on its
 * own day removes the record entirely.
 */
export const scheduleExceptionsApi = {
  async getAll(): Promise<ScheduleException[]> {
    const rows = await apiRequest<any[]>('/schedule-exceptions');
    return rows.map(r => ({
      ...r,
      originalDate: new Date(r.originalDate),
      newDate: new Date(r.newDate),
    }));
  },

  async move(
    kind: ScheduleException['kind'],
    sourceId: string,
    originalDate: Date,
    newDate: Date
  ): Promise<void> {
    await apiRequest<any>('/schedule-exceptions', {
      method: 'PUT',
      body: JSON.stringify({
        kind,
        sourceId,
        originalDate: originalDate.toISOString(),
        newDate: newDate.toISOString(),
      }),
    });
  },
};

// Backup API
export const backupApi = {
  async createBackup(): Promise<{ 
    success: boolean; 
    message: string; 
    output: string; 
    hasChanges: boolean; 
  }> {
    return apiRequest('/backup', {
      method: 'POST',
    });
  },

  async getBackupStatus(): Promise<{
    success: boolean;
    recentBackups: string[];
    lastBackup: string | null;
  }> {
    return apiRequest('/backup/status');
  },
};

// Loans API
export const loansApi = {
  async getAll(): Promise<Loan[]> {
    return apiRequest<Loan[]>('/loans');
  },

  async getById(id: string): Promise<Loan> {
    return apiRequest<Loan>(`/loans/${id}`);
  },

  async create(loanData: any): Promise<Loan> {
    return apiRequest<Loan>('/loans', {
      method: 'POST',
      body: JSON.stringify(loanData),
    });
  },

  async update(id: string, loanData: any): Promise<Loan> {
    return apiRequest<Loan>(`/loans/${id}`, {
      method: 'PUT',
      body: JSON.stringify(loanData),
    });
  },

  async delete(id: string): Promise<void> {
    return apiRequest<void>(`/loans/${id}`, {
      method: 'DELETE',
    });
  },
};

// Admin API
export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  createdAt: string;
  updatedAt: string;
  _count: {
    bills: number;
    incomes: number;
    creditCards: number;
    loans: number;
  };
}

export const adminApi = {
  async getAllUsers(): Promise<AdminUser[]> {
    return apiRequest<AdminUser[]>('/admin/users');
  },
};

// --- Settings (SMTP + reminders) ---

export interface SmtpSettings {
  host: string;
  port: number;
  secure: boolean;
  username: string;
  fromAddress: string;
  fromName?: string | null;
  updatedAt?: string;
}

export interface ReminderSettings {
  enabled: boolean;
  daysBefore: number;
  sendHour: number;
  remindBills: boolean;
  billCategories: string[] | null; // null = all categories
  remindCreditCards: boolean;
  remindLoans: boolean;
}

export const settingsApi = {
  async getSmtp(): Promise<SmtpSettings | null> {
    return apiRequest<SmtpSettings | null>('/settings/smtp');
  },
  async saveSmtp(payload: Partial<SmtpSettings> & { password?: string }): Promise<SmtpSettings> {
    return apiRequest<SmtpSettings>('/settings/smtp', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  async deleteSmtp(): Promise<void> {
    return apiRequest<void>('/settings/smtp', { method: 'DELETE' });
  },
  async testSmtp(): Promise<{ messageId: string }> {
    return apiRequest<{ messageId: string }>('/settings/smtp/test', { method: 'POST' });
  },
  async getReminders(): Promise<ReminderSettings | null> {
    return apiRequest<ReminderSettings | null>('/settings/reminders');
  },
  async saveReminders(payload: ReminderSettings): Promise<ReminderSettings> {
    return apiRequest<ReminderSettings>('/settings/reminders', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
};

export default {
  auth: authApi,
  user: userApi,
  expenses: expensesApi,
  income: incomeApi,
  creditCards: creditCardsApi,
  loans: loansApi,
  payoffPlans: payoffPlansApi,
  backup: backupApi,
  admin: adminApi,
  settings: settingsApi,
};